import {
  IInsight,
  IInsightTip,
  IInsightWidget,
  InsightType,
  OfferSubType,
  OfferType,
  Severity,
  SeverityState
} from '@jumpstart/core';
import { WidgetType } from '@jumpstart/core/dist/schemas/insight/insight';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { headerCase } from 'change-case';
import { FieldArray, Form, FormikProps } from 'formik';
import React from 'react';
import { formStyles } from '../styles';
import ContentBaseField from './ContentBaseField';
import ContentField from './ContentField';
import { CTEXT } from './DialogFormConfig';
import EventFormImageField from './EventFormImageField';
import { IValidation } from './utils';
import WidgetField from './WidgetField';
import WidgetMultiFields from './WidgetMultiFields';

export default function EventFormEdit(props: FormikProps<IInsight> & { validation: IValidation }) {
  const { errors, handleSubmit, values, setFieldValue, handleChange, touched, validation } = props;

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(e);
      }}
    >
      <Grid
        direction="column"
        container
        alignItems="center"
        justifyContent="center"
        sx={{
          maxWidth: 850,
          ...formStyles.section
        }}
      >

        <Collapse in={Object.keys(errors).length > 0}>
          <Alert severity="error">
            <Grid container direction="column">
              {
                Object
                  .keys(errors)
                  .map((key: string) => <Typography
                      key={key}
                      variant="caption"
                      color="textPrimary"
                      style={{ marginBottom: 8 }}
                    >
                      <b>event.{key}</b>: {(errors as any)[key]}
                    </Typography>
                  )
              }
            </Grid>
          </Alert>
        </Collapse>
        <Grid item container>
          <FormControlLabel
            control={<Switch
              checked={(values as any).active}
              onChange={(event) => {
                setFieldValue('active', event.target.checked);
              }}
              name="action"
            />}
            label={(values as any).active ? 'Active' : 'Inactive'}
          />
        </Grid>

        <EventFormImageField eventId={values.id}/>

        <TextField
          id="id"
          name="name"
          value={values.id}
          label="id"
          sx={formStyles.input}
          variant="outlined"
          disabled
        />

        <ContentField
          id="name"
          name="name"
          defaultValue={values.name}
          onBlur={handleChange}
          sx={formStyles.input}
          variant="outlined"
          label="Event Name"
          error={!!errors.name}
          helperText={!errors.name ? `ID: ${values.id}` : errors.name}
          validation={validation}
          touched={!!touched.name}
        />
        <ContentField
          id="description"
          name="description"
          defaultValue={values.description}
          onBlur={handleChange}
          sx={formStyles.input}
          variant="outlined"
          label="Event Description"
          multiline
          error={!!errors.description}
          helperText={!errors.description ? CTEXT(
            values.description,
            'body'
          ) : `${errors.description} ${CTEXT(values.description, 'body')}`}
          validation={validation}
          touched={!!touched.description}
        />

        <FormControl sx={formStyles.input}>
          <InputLabel id="type-label" style={{ zIndex: 2, top: -8, left: 14 }}>Type</InputLabel>
          <Select
            id="type"
            name="type"
            value={values.type}
            onChange={handleChange}
            variant="outlined"
          >
            {[
              'TRAINING',
              'BEHAVIOR',
              'HEALTH',
              'NUTRITION',
              'ENVIRONMENT',
              ...Object.keys(InsightType)
            ].map((type: string) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
          </Select>
        </FormControl>


        <TextField
          id="severity"
          name="severity"
          label="Severity"
          sx={formStyles.input}
          value={values.severity}
          onChange={handleChange}
          variant="outlined"
          select
          error={!!errors.severity}
          helperText={!errors.severity ? '' : errors.severity}
        >
          {Object.keys(Severity)
            .map((severity: string) => <MenuItem key={severity} value={severity}>{severity}</MenuItem>)}
        </TextField>

        {/*<FieldArray name="tags">*/}
        {/*  {(tagsArrayHelper) => (*/}
        {/*    <Grid container direction="column" sx={formStyles.input}>*/}
        {/*      {values.tags && values.tags.map((tag: string, index: number) =>*/}
        {/*        <Grid container direction="row" wrap="nowrap">*/}
        {/*          <TextField*/}
        {/*            id={`tags[${index}]`}*/}
        {/*            name="tags"*/}
        {/*            defaultValue={tag}*/}
        {/*            onBlur={handleChange}*/}
        {/*            sx={formStyles.input}*/}
        {/*            variant="outlined"*/}
        {/*            label={`Tag ${index + 1}`}*/}
        {/*          />*/}
        {/*          <IconButton onClick={() => tagsArrayHelper.remove(index)}*/}
        {/*                      style={{ width: 56 }}><RemoveIcon/></IconButton>*/}
        {/*        </Grid>*/}
        {/*      )}*/}
        {/*      <Grid item container direction="row" wrap="nowrap" alignItems="center" style={{ marginTop: 12 }}>*/}
        {/*        <Button onClick={() => tagsArrayHelper.push('')} startIcon={<AddIcon/>}>Tag</Button>*/}
        {/*      </Grid>*/}
        {/*    </Grid>*/}
        {/*  )}*/}
        {/*</FieldArray>*/}

        <Accordion sx={formStyles.input} >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            <Grid container direction="column">
              <Typography variant="body1">Time Variables</Typography>
              <Typography variant="caption" color={'textSecondary'}>
                Whether this event should be suggested on a dog's timeline, or does it reoccur?
              </Typography>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container direction="column">
              <TextField
                id="timelineStart"
                name="timelineStart"
                defaultValue={values.timelineStart}
                onBlur={handleChange}
                sx={formStyles.input}
                variant="outlined"
                label={`Recommended Timeline Start`}
                type="number"
                error={!!errors.timelineStart}
                helperText={!errors.timelineStart ? 'Days old until this event should start' : errors.timelineStart}
              />
              <TextField
                id="timelineEnd"
                name="timelineEnd"
                defaultValue={values.timelineEnd}
                onBlur={handleChange}
                sx={formStyles.input}
                variant="outlined"
                label={`Recommended Timeline End`}
                type="number"
                error={!!errors.timelineEnd}
                helperText={!errors.timelineEnd ? 'Days old until this event should end' : errors.timelineEnd}
              />
              <TextField
                id="timelineBreedOffset"
                name="timelineBreedOffset"
                defaultValue={values.timelineBreedOffset}
                onBlur={handleChange}
                sx={formStyles.input}
                variant="outlined"
                label={`Timeline Breed Offset`}
                error={!!errors.timelineBreedOffset}
                helperText={!errors.timelineBreedOffset ? 'Days old until this event should offset by the breed' : errors.timelineBreedOffset}
                type="number"
              />
              <TextField
                id="recurringValue"
                name="recurringValue"
                defaultValue={values.recurringValue}
                onBlur={handleChange}
                sx={formStyles.input}
                variant="outlined"
                label={`Recurring Value`}
                type="number"
                error={!!errors.recurringValue}
                helperText={!errors.recurringValue ? 'Days old until this event would reoccur' : errors.recurringValue}
              />
            </Grid>
            <FormControl sx={formStyles.input}>
              <InputLabel id="recurring-unit-label" style={{ zIndex: 2, top: -8, left: 14 }}>Recurring Unit</InputLabel>
              <Select
                id="recurringUnit"
                name="recurringUnit"
                value={values.recurringUnit}
                onChange={handleChange}
                variant="outlined"
              >
                {['days', 'weeks', 'months', 'years'].map((time: string) => <MenuItem key={time}
                                                                                      value={time}>{time}</MenuItem>)}
              </Select>
            </FormControl>
          </AccordionDetails>
        </Accordion>

        <Accordion sx={formStyles.input} >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            <Grid container direction="column">

              <Typography variant="body1">Additional Context
                ({(values.highlights ? values.highlights : []).length})</Typography>
              <Typography variant="caption" color={errors.highlights ? 'error' : 'textSecondary'}>
                {errors.highlights ? errors.highlights : 'Additional context to give the user (definitions, etc)'}
              </Typography>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <FieldArray
              name={`highlights`}>
              {(arrayHelpers) =>
                <Grid container direction="column" wrap="nowrap">
                  {(values.highlights ? values.highlights : []).map((highlight: string, index: number) =>
                    <ContentField
                      key={`highlights[${index}]`}
                      id={`highlights[${index}]`}
                      name={`highlights[${index}]`}
                      defaultValue={highlight}
                      onBlur={handleChange}
                      sx={formStyles.input}
                      style={{ width: '100%' }}
                      variant="outlined"
                      validation={validation}
                      touched={!!touched.highlights}
                      multiline
                      helperText={`${values.highlights && CTEXT(values.highlights[index], 'highlights')}`}
                      // InputLabelProps={{shrink: true}}
                      InputProps={
                        {
                          notched: true,
                          additionalEndAdornments: <IconButton
                            size="small"
                            onClick={() => arrayHelpers.remove(index)}
                          >
                            <CloseIcon/>
                          </IconButton>
                        }
                      }
                    />
                  )}
                  <Button startIcon={<AddIcon/>} onClick={() => arrayHelpers.push('')}>More</Button>
                </Grid>
              }
            </FieldArray>
          </AccordionDetails>
        </Accordion>


        <Accordion sx={formStyles.input} >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            <Grid container direction="column">
              <Typography variant="body1">💎 Widgets
                ({(values.widgets ? values.widgets : []).length})</Typography>
              <Typography variant="caption" color="textSecondary">Additional widgets to display to the
                user (products, services, insights)</Typography>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container direction="column">

              {values.widgets && values.widgets.length > 0 &&
              <WidgetMultiFields
                values={values}
                classes={formStyles}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
              />
              }
              <FieldArray
                name={`widgets`}>
                {(arrayHelpers) =>
                  <Grid container direction="column" wrap="nowrap">
                    {(values.widgets ? values.widgets : []).map((widget: IInsightWidget, index: number) =>
                      <WidgetField values={values} widget={widget} index={index} classes={formStyles}
                                   arrayHelpers={arrayHelpers} handleChange={handleChange}
                                   setFieldValue={setFieldValue}
                                   touched={touched}
                                   validation={validation}
                      />
                    )}
                    <Button
                      startIcon={<AddIcon/>}
                      onClick={() => {
                        const defaultWidget: IInsightWidget = {
                          widget: WidgetType.PRODUCT,
                          requirements: [],
                          purpose: '',
                          title: '',
                          meta: {
                            type: OfferType.PRODUCT,
                            subtype: OfferSubType.UNKNOWN,
                            severity: Severity.MINOR
                          }
                        };
                        arrayHelpers.push(defaultWidget);
                      }}
                    >
                      Add Widget
                    </Button>
                  </Grid>
                }
              </FieldArray>

            </Grid>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={formStyles.input} >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon/>}
          >
            <Grid container direction="column">
              <Typography variant="body1">❕ Tips ({(values.tips ? values.tips : []).length})</Typography>
              <Typography variant="caption"
                          color={errors.tips ? 'error' : 'textSecondary'}>{errors.tips ? errors.tips : 'Helpful tips to display to the user'}</Typography>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <FieldArray
              name={`tips`}
            >
              {(tipArrayHelpers) =>
                <Grid container direction="column" wrap="nowrap">
                  {(values.tips ? values.tips : []).map((tip: IInsightTip, index: number) =>
                    <Grid key={`tips[${index}]`} container direction="row" wrap="nowrap"
                          sx={formStyles.input}>

                      <ContentField
                        key={`tips[${index}].message`}
                        id={`tips[${index}].message`}
                        name={`tips[${index}].message`}
                        label={`Tip ${index}`}
                        defaultValue={tip.message}
                        onBlur={handleChange}
                        sx={formStyles.input}
                        variant="outlined"
                        placeholder="Tip"
                        multiline
                        validation={validation}
                        touched={!!touched.tips}
                        helperText={`${values.tips && CTEXT(values.tips[index].message, 'tips')}`}
                        InputProps={
                          {
                            additionalEndAdornments: <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => tipArrayHelpers.remove(index)}><CloseIcon/></IconButton>
                            </InputAdornment>
                          }
                        }

                      />

                      <ContentBaseField
                        sx={formStyles.input}
                        key={`tips[${index}].severity`}
                        id={`tips[${index}].severity`}
                        name={`tips[${index}].severity`}
                        label={`Severity`}
                        value={tip.severity}
                        onChange={handleChange}
                        select
                        variant="outlined"
                        validation={validation}
                        touched={!!touched.tips}
                      >
                        {
                          Object.keys(SeverityState)
                            .map((key: string) => <MenuItem
                                key={`tips[${index}].severity.${key}`}
                                value={key}
                              >
                                {headerCase(key)}
                              </MenuItem>
                            )
                        }
                      </ContentBaseField>
                      {/*<Grid item>*/}
                      {/*  <IconButton onClick={() => tipArrayHelpers.remove(index)}><CloseIcon/></IconButton>*/}
                      {/*</Grid>*/}
                    </Grid>
                  )}
                  <Button startIcon={<AddIcon/>} onClick={() => {
                    console.log('Adding tip...');
                    tipArrayHelpers.push({
                      severity: SeverityState.INFO,
                      message: ''
                    });
                  }
                  }>Add Tip</Button>
                </Grid>
              }
            </FieldArray>
          </AccordionDetails>
        </Accordion>


        <Button variant="outlined" type="submit" color="primary"
                style={{ maxWidth: 550 }}>Save</Button>
      </Grid>
    </Form>
  );
}
